/**
 * /workspace/sell/inventory — stock + reorder over products.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, AlertTriangle, Boxes, IndianRupee, Search, Loader2, Save, Filter } from "lucide-react";
import { toast } from "sonner";

interface Item {
  product_id: string;
  title: string;
  category?: string;
  price?: number;
  cost_price?: number;
  sku?: string;
  stock_on_hand?: number;
  reorder_point?: number;
  status?: "ok" | "low" | "out";
  value_on_hand?: number;
}

export default function SellInventory() {
  const [q, setQ] = React.useState("");
  const [lowOnly, setLowOnly] = React.useState(false);
  const [drafts, setDrafts] = React.useState<Record<string, number>>({});
  const qc = useQueryClient();

  const inv = useQuery<{ items: Item[]; total: number; kpi: any }>({
    queryKey: ["sell-inventory", q, lowOnly],
    queryFn: async () => (await api.get(`/workspace/sell/inventory?q=${encodeURIComponent(q)}&low_stock=${lowOnly ? 1 : 0}&limit=100`)).data,
  });

  const save = useMutation({
    mutationFn: async ({ pid, stock }: { pid: string; stock: number }) => (await api.post(`/workspace/sell/inventory/${pid}/stock`, { stock_on_hand: stock })).data,
    onSuccess: (_d, v) => {
      toast.success("Stock updated.");
      setDrafts((s) => { const c = { ...s }; delete c[v.pid]; return c; });
      qc.invalidateQueries({ queryKey: ["sell-inventory"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Update failed"),
  });

  const k = inv.data?.kpi;
  return (
    <DashboardLayout title="Inventory" subtitle="Stock on hand, reorder alerts and inventory value — updates land in the shop instantly.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Products" value={k?.products ?? "—"} icon={Package} tone="blue"/>
        <KpiCard label="Out of stock" value={k?.out_of_stock ?? "—"} icon={AlertTriangle} tone="rose"/>
        <KpiCard label="Low stock" value={k?.low_stock ?? "—"} icon={Boxes} tone="amber"/>
        <KpiCard label="Inventory value" value={inr(k?.total_value)} icon={IndianRupee} tone="emerald"/>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
        <button
          data-testid="inv-low-toggle"
          onClick={() => setLowOnly((v) => !v)}
          className={`h-8 px-3 rounded-full text-xs font-semibold border ${lowOnly ? "bg-amber-500 text-white border-transparent" : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"}`}
        >
          Low stock only
        </button>
        <div className="ml-auto flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
          <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, SKU, category…" className="h-8 w-64"/>
        </div>
      </div>

      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden">
        {inv.isLoading ? (
          <div className="p-8 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/> Loading…</div>
        ) : (
          <div className="overflow-x-auto sg-scrollbar-thin">
            <table className="w-full text-sm" data-testid="inv-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                  <th className="text-left py-2 pl-4">Product</th>
                  <th className="text-left py-2">SKU</th>
                  <th className="text-right py-2">Cost</th>
                  <th className="text-right py-2">Price</th>
                  <th className="text-right py-2">Stock</th>
                  <th className="text-left py-2">Health</th>
                  <th className="text-right py-2 pr-4">Update</th>
                </tr>
              </thead>
              <tbody>
                {inv.data!.items.map((it) => {
                  const draft = drafts[it.product_id];
                  const stock = draft ?? it.stock_on_hand ?? 0;
                  const dirty = draft !== undefined && draft !== it.stock_on_hand;
                  const busy = save.isPending && save.variables?.pid === it.product_id;
                  return (
                    <tr key={it.product_id} className="border-b border-[color:var(--sg-border)]/60 hover:bg-[color:var(--sg-muted-bg)]/40" data-testid={`inv-row-${it.product_id}`}>
                      <td className="py-2 pl-4 max-w-[320px]"><div className="truncate text-[color:var(--sg-fg)] font-medium">{it.title}</div><div className="text-[10px] text-[color:var(--sg-muted)]">{it.category || "—"}</div></td>
                      <td className="py-2 font-mono text-[11px] text-[color:var(--sg-fg)]">{it.sku || "—"}</td>
                      <td className="py-2 text-right tabular-nums text-[color:var(--sg-muted)]">{inr(it.cost_price)}</td>
                      <td className="py-2 text-right tabular-nums font-semibold text-[color:var(--sg-fg)]">{inr(it.price)}</td>
                      <td className="py-2 text-right">
                        <Input
                          data-testid={`inv-stock-${it.product_id}`}
                          type="number"
                          value={stock}
                          min={0}
                          onChange={(e) => setDrafts((s) => ({ ...s, [it.product_id]: Math.max(0, Number(e.target.value) || 0) }))}
                          className={`h-8 w-24 text-right tabular-nums ${dirty ? "border-amber-500 ring-1 ring-amber-500" : ""}`}
                        />
                      </td>
                      <td className="py-2"><HealthBadge status={it.status}/></td>
                      <td className="py-2 pr-4 text-right">
                        {dirty && (
                          <Button
                            data-testid={`inv-save-${it.product_id}`}
                            size="sm"
                            className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={busy}
                            onClick={() => save.mutate({ pid: it.product_id, stock })}
                          >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Save className="h-3 w-3 mr-1"/> Save</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function inr(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function KpiCard({ label, value, icon: Icon, tone }: { label: string; value: any; icon: any; tone: string }) {
  const t: Record<string, string> = {
    blue:    "text-blue-600 dark:text-blue-300 from-blue-500/15",
    rose:    "text-rose-600 dark:text-rose-300 from-rose-500/15",
    amber:   "text-amber-600 dark:text-amber-300 from-amber-500/15",
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
  };
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${t[tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${t[tone].split(" ")[0]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {label}
        </div>
        <div className="mt-1.5 font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function HealthBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    ok:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    low: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    out: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  };
  const label = status === "ok" ? "Healthy" : status === "low" ? "Low" : status === "out" ? "Out" : "—";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status || "ok"]}`}>{label}</span>;
}
